/**
 * Tasks (owner, 12 Oct 2026: "create a task assignment system for me i will
 * assign the tasks to the employee and all follow up will be done on the app
 * only").
 *
 * He chose: only he assigns; the employee's "done" comes back to him to check
 * and close, or to send back; tasks can repeat daily, weekly or monthly; and
 * they go to employees only — never to a mitr, outlet or dealer's counter.
 */
import { HEAD_ADMIN_DESK_EMAILS, isVerifiedEmployee, type PersonRuleInput } from './domain.js';

export const TASK_STATUSES = ['OPEN', 'IN_PROGRESS', 'WAITING_CHECK', 'CLOSED', 'CANCELLED'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Still the employee's to do. */
export const TASK_OPEN_STATUSES: readonly TaskStatus[] = ['OPEN', 'IN_PROGRESS'];

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  OPEN: 'To do',
  IN_PROGRESS: 'Working on it',
  WAITING_CHECK: 'Waiting for check',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled',
};

export const TASK_PRIORITIES = ['NORMAL', 'URGENT'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const TASK_CADENCES = ['DAILY', 'WEEKLY', 'MONTHLY'] as const;
export type TaskCadence = (typeof TASK_CADENCES)[number];

export const TASK_CADENCE_LABELS: Record<TaskCadence, string> = {
  DAILY: 'Every day',
  WEEKLY: 'Every week',
  MONTHLY: 'Every month',
};

export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export const TASK_EVENT_KINDS = ['CREATED', 'COMMENT', 'STARTED', 'DONE', 'CLOSED', 'SENT_BACK', 'CANCELLED', 'REOPENED', 'EDITED'] as const;
export type TaskEventKind = (typeof TASK_EVENT_KINDS)[number];

/**
 * How far each person is with what they were given (owner, 19 Oct 2026: "add
 * a completion bar sort of element in the task manager"). Given: the tasks due
 * in the period, and the undated ones given in it, cancelled aside. Done: the
 * employee finished them (waiting for the owner's check, or closed). Checked:
 * the owner closed them. Overdue before: still open from an earlier due date.
 */
export const TASK_PROGRESS_PERIODS = ['today', 'week'] as const;
export type TaskProgressPeriod = (typeof TASK_PROGRESS_PERIODS)[number];

export interface TaskProgressCount {
  given: number;
  done: number;
  checked: number;
  overdueBefore: number;
}

export interface TaskProgress {
  period: TaskProgressPeriod;
  /** IST days, inclusive. */
  from: string;
  to: string;
  overall: TaskProgressCount;
  people: Array<TaskProgressCount & { id: string; name: string }>;
}

/** Done out of given, as a whole percentage; nothing given reads as nothing to do, not as 0%. */
export function taskProgressPercent(count: Pick<TaskProgressCount, 'given' | 'done'>): number | null {
  return count.given > 0 ? Math.round((count.done / count.given) * 100) : null;
}

/** Who gives tasks, checks them and closes them: the owner's head-admin sign-ins, and no one else. */
export function isTaskAssigner(user: { email: string } | null | undefined): boolean {
  return Boolean(user) && HEAD_ADMIN_DESK_EMAILS.includes(user!.email.trim().toLowerCase());
}

/**
 * Roles that are employees but never given tasks (owner, 12 Oct 2026: "remove
 * drivers from tasks they are irrelevant for the app only office employee and
 * asm will show up there"). A driver's sign-in shows their deliveries and
 * nothing else.
 */
export const TASK_EXCLUDED_ROLES: readonly string[] = ['DRIVER'];

/** Who can be given a task: the office's employees and the ASMs — never a counter, a head-admin desk or a driver. */
export function isTaskAssignee(user: PersonRuleInput): boolean {
  return isVerifiedEmployee(user) && !TASK_EXCLUDED_ROLES.includes(user.roleKey);
}

/**
 * Employee to employee (owner, 20 Oct 2026: "create a employee to employee
 * task manager"; asked who gives tasks to whom: "Anyone to anyone"). Every
 * employee who can be given a task can give one to another; the person who
 * GAVE it checks the "done" and closes it or sends it back. The owner's desks
 * still give to anyone, see every task and may check any. Repeating tasks stay
 * the owner's — an employee gives one-off tasks.
 */
export function givesTasks(user: PersonRuleInput | null | undefined): boolean {
  return Boolean(user) && (isTaskAssigner(user) || isTaskAssignee(user!));
}

/** Who checks, closes, sends back, edits or cancels a task: whoever gave it, and the owner's desks. */
export function checksTask(viewer: { id: string; email: string }, task: { assignedById: string }): boolean {
  return isTaskAssigner(viewer) || task.assignedById === viewer.id;
}

/** The task list's three views: given to me, given by me, and — the owner's desks only — everyone's. */
export const TASK_VIEWS = ['mine', 'given', 'all'] as const;
export type TaskView = (typeof TASK_VIEWS)[number];
export const TASK_VIEW_LABELS: Record<TaskView, string> = { mine: 'For me', given: 'I gave', all: 'Everyone' };

/** "HH:MM", 24-hour, the way a repeating task's due time is kept. */
export const TASK_DUE_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The longest a title or a follow-up note may run. */
export const TASK_TITLE_MAX = 160;
export const TASK_NOTE_MAX = 2000;
